import { useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../../../app/store';
import { addInsuranceAction as addInsurance } from '../../../../../../services/EncounterService/slice';
import { useGetPayersQuery } from '../../../../../../services/ConfigurationService';
import { useInsuranceAddForm } from './useInsuranceAddForm';

interface UseInsuranceAddDialogProps {
  onInsuranceAdded?: () => void;
}

export const useInsuranceAddDialog = ({ onInsuranceAdded }: UseInsuranceAddDialogProps = {}) => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const encounter = useAppSelector(state => state.encounter.currentEncounter);
  
  // Check if encounter is cancelled
  const isEncounterCancelled = encounter?.status === 'Cancelled';

  // Get center ID from URL parameters
  const company = searchParams.get('company') || '';
  const center = searchParams.get('center') || '';
  const centerId = company && center ? `${company}-${center}` : '';

  // Fetch payers from configuration API
  const { data: payers = [] } = useGetPayersQuery(
    { company, centerId },
    { skip: !company || !centerId }
  );

  // Dialog open state
  const [open, setOpen] = useState(false);

  // Form state
  const formState = useInsuranceAddForm();

  const handleOpen = useCallback(() => {
    formState.resetForm();
    setOpen(true);
  }, [formState]);

  const handleClose = useCallback(() => {
    setOpen(false);
    formState.resetForm();
  }, [formState]);

  const handleSubmit = useCallback(() => {
    if (!formState.validateForm()) return;

    const newInsurance = formState.createInsuranceData();
    dispatch(addInsurance(newInsurance));
    onInsuranceAdded?.();
    handleClose();
  }, [dispatch, formState, handleClose, onInsuranceAdded]);

  return {
    open,
    payers,
    isEncounterCancelled,
    handleOpen,
    handleClose,
    handleSubmit,
    ...formState,
  };
};
